import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Stack
} from '@mui/material';
import { Google as GoogleIcon, MailOutline as MailIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import {
  DEPLOYMENT_CONFIG,
  isFirebaseEmailPasswordEnabled,
  isGoogleWorkspaceEnabled
} from '../config/deploymentConfig';

function Login() {
  const { signInWithGoogle, signInWithEmail, sendPasswordReset, error, loading } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [emailSigningIn, setEmailSigningIn] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localMessage, setLocalMessage] = useState(null);
  const workspaceDomain = DEPLOYMENT_CONFIG.workspace.domain;
  const adminGroup = DEPLOYMENT_CONFIG.workspace.groups.admins;
  const inspectorGroup = DEPLOYMENT_CONFIG.workspace.groups.inspectors;
  const googleEnabled = isGoogleWorkspaceEnabled();
  const emailPasswordEnabled = isFirebaseEmailPasswordEnabled();
  const { appName, companyName, loginLogoPath, organizationName, tagline } = DEPLOYMENT_CONFIG.labels;

  const handleGoogleSignIn = async () => {
    setSigningIn(true);
    try {
      await signInWithGoogle(false);
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setSigningIn(false);
    }
  };

  const handleEmailSignIn = async (event) => {
    event.preventDefault();
    setEmailSigningIn(true);
    setLocalMessage(null);
    try {
      await signInWithEmail(email, password);
    } catch (err) {
      console.error('Email login error:', err);
    } finally {
      setEmailSigningIn(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email.trim()) {
      setLocalMessage({ severity: 'warning', text: 'Enter your email address first.' });
      return;
    }

    setResettingPassword(true);
    setLocalMessage(null);
    try {
      await sendPasswordReset(email);
      setLocalMessage({ severity: 'success', text: 'If that account exists, a password reset email will be sent.' });
    } catch (err) {
      console.error('Password reset error:', err);
    } finally {
      setResettingPassword(false);
    }
  };

  // Detect problematic browsers
  const detectProblematicBrowser = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for Facebook browser
    if (ua.indexOf('FBAN') > -1 || ua.indexOf('FBAV') > -1) {
      return 'Facebook';
    }
    
    // Check for Instagram browser
    if (ua.indexOf('Instagram') > -1) {
      return 'Instagram';
    }
    
    // Check for LinkedIn browser
    if (ua.indexOf('LinkedInApp') > -1) {
      return 'LinkedIn';
    }
    
    // Check for Twitter browser
    if (ua.indexOf('Twitter') > -1) {
      return 'Twitter';
    }
    
    // Check for WeChat
    if (ua.indexOf('MicroMessenger') > -1) {
      return 'WeChat';
    }
    
    return null;
  };
  
  const problematicBrowser = detectProblematicBrowser();

  return (
    <Box sx={{ width: '100%', maxWidth: 400 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          {/* Browser Warning */}
          {problematicBrowser && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                <strong>⚠️ {problematicBrowser} Browser Detected</strong>
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Google sign-in may not work in this browser. Please:
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                1. Tap the menu (⋮ or ...)
                <br />
                2. Select "Open in Browser" or "Open in Safari/Chrome"
                <br />
                3. Or copy this link: <strong>{window.location.href}</strong>
              </Typography>
            </Alert>
          )}
          {/* Brand Logo Area */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img
              src={loginLogoPath}
              alt={`${appName} Logo`}
              style={{
                width: '220px',
                maxWidth: '75%',
                height: 'auto',
                marginBottom: '16px'
              }}
              onError={(e) => {
                // Hide image if it fails to load
                e.target.style.display = 'none';
              }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              {appName}
            </Typography>
            <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600 }}>
              {organizationName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {tagline}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Sign In Section */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Sign In to Continue
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {googleEnabled && emailPasswordEnabled
                ? 'Use an authorized account'
                : googleEnabled
                ? `Use your @${workspaceDomain} Google account`
                : 'Use your assigned email and password'}
            </Typography>

            {/* Status Display */}
            {error && (
              <Alert 
                severity={error === 'Determining access permissions...' ? 'info' : 'error'} 
                sx={{ mb: 3 }}
              >
                {error === 'Determining access permissions...'
                  ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2">
                        Determining access permissions...
                      </Typography>
                    </Box>
                  )
                  : error.includes('popup-blocked') 
                  ? 'Popup blocked. Please allow popups or use the mobile sign-in option.'
                  : error.includes('cancelled-popup-request')
                  ? 'Sign-in was cancelled. Please try again.'
                  : error.includes('Access denied')
                  ? (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Authentication Failed</strong>
                      </Typography>
                      <Typography variant="caption" display="block">
                        {error}
                      </Typography>
                    </Box>
                  )
                  : error.includes('secure') || error.includes('disallowed_useragent')
                  ? (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Browser Security Issue Detected</strong>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Google requires a secure browser. Please try:
                      </Typography>
                      <Typography variant="caption" component="ul" sx={{ textAlign: 'left', mt: 1 }}>
                        <li>Open in Chrome, Safari, or Firefox</li>
                        <li>Copy this link: {window.location.href}</li>
                        <li>If in Facebook/Instagram, tap "..." → "Open in Browser"</li>
                        <li>On iPhone: Use Safari instead of app browsers</li>
                      </Typography>
                    </Box>
                  )
                  : error.includes('internal-error')
                  ? (
                    <Box>
                      <Typography variant="body2" gutterBottom>
                        <strong>Authentication Error</strong>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Please try the following:
                      </Typography>
                      <Typography variant="caption" component="ul" sx={{ textAlign: 'left', mt: 0.5, pl: 2 }}>
                        <li>Clear your browser cookies and cache</li>
                        <li>Disable browser extensions temporarily</li>
                        <li>Try opening in an incognito/private window</li>
                        <li>Use a different browser (Chrome, Safari, Firefox)</li>
                      </Typography>
                    </Box>
                  )
                  : `Sign-in error: ${error}`
                }
              </Alert>
            )}

            {localMessage && (
              <Alert severity={localMessage.severity} sx={{ mb: 3 }} onClose={() => setLocalMessage(null)}>
                {localMessage.text}
              </Alert>
            )}

            {/* Google Sign In Button */}
            {googleEnabled && (
              <Button
                variant="contained"
                size="large"
                startIcon={
                  signingIn || loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <GoogleIcon />
                  )
                }
                onClick={handleGoogleSignIn}
                disabled={signingIn || loading || emailSigningIn}
                sx={{
                  minHeight: '48px',
                  backgroundColor: '#4285f4',
                  '&:hover': {
                    backgroundColor: '#3367d6',
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                  },
                }}
              >
                {signingIn || loading
                  ? 'Signing in...'
                  : 'Sign in with Google'
                }
              </Button>
            )}

            {googleEnabled && emailPasswordEnabled && (
              <Divider sx={{ my: 3 }}>or</Divider>
            )}

            {emailPasswordEnabled && (
              <Box component="form" onSubmit={handleEmailSignIn} sx={{ mt: googleEnabled ? 0 : 1 }}>
                <Stack spacing={2}>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    fullWidth
                    size="small"
                    disabled={emailSigningIn || loading}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    fullWidth
                    size="small"
                    disabled={emailSigningIn || loading}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={
                      emailSigningIn || loading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <MailIcon />
                      )
                    }
                    disabled={emailSigningIn || loading || signingIn || !email.trim() || !password}
                    sx={{ minHeight: '48px' }}
                  >
                    {emailSigningIn || loading ? 'Signing in...' : 'Sign in'}
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    onClick={handlePasswordReset}
                    disabled={resettingPassword || emailSigningIn || loading || !email.trim()}
                  >
                    {resettingPassword ? 'Sending reset...' : 'Reset password'}
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Help Text */}
            {googleEnabled && (
              <Box sx={{ mt: 4, p: 2, backgroundColor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Having Trouble Signing In?</strong>
              </Typography>
              
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                <strong>If you see "doesn't comply with secure browser":</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" component="ul" sx={{ mt: 0.5, pl: 2 }}>
                <li>Open in Chrome, Safari, or Firefox directly</li>
                <li>Don't use app browsers (Facebook, Instagram, etc.)</li>
                <li>On iPhone: Use Safari, not Chrome</li>
                <li>Clear browser cache and cookies</li>
              </Typography>
              
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                <strong>Need Access?</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Contact your administrator to be added to:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                • {adminGroup} (Admin)<br />
                • {inspectorGroup} (Inspector)
              </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Privacy Notice and Footer */}
      <Card sx={{ mt: 2, backgroundColor: 'grey.50' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="caption" color="error" align="center" display="block" sx={{ fontWeight: 'bold' }}>
            PRIVATE BUSINESS APPLICATION - AUTHORIZED USERS ONLY
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
            This is the {appName} application for authorized users only.
            This is NOT a public service. Unauthorized access is strictly prohibited and will be reported.
          </Typography>
          <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 1 }}>
            By logging in, you confirm you are an authorized user with valid access permissions.
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            © 2026 {companyName} - Internal Use Only
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default Login;
